import React, { useState } from "react";
import { defaultStepPropertyTypeListEnum, PropertyCcpInputTypeListEnum, PropertyCpInputTypeListEnum, propertyList } from "assets/constants";
import { Select, Input, popperProps } from "./styles";
import { MenuItem } from "@mui/material";
import { MobileDatePicker, MultiLineText, SearchField, CheckBox } from "components/core";
import { isBoolean } from "utils";
import clsx from "clsx";

//helper function for select inputs
const chk = (input) => input === undefined ? true : input;

// Helper function to validate a field if it's not required
const validateIfNotRequired = (input, required) => input === undefined ? !required : input;

/*
Helper injects values to input fields without triggering touch
The complexity here is that we dont actually know what property is for example firstName or lastName
in the form those fields are market by identificator 17_2 or 18_4 and so on
here we need to connect the data that we receive from customer to identificators
*/
const DetailsDataInjector = (type, property, customerDetails, validationObject) => {
    const propertyType = property.type;
    let identificator;
    //extract identificator based on cp or ccp
    switch(type) {
        case propertyList.cp: identificator = `${type}_${propertyType}`; break;
        case propertyList.ccp:  identificator = `${type}_${property.id}`; break;
        default: break;
    }

    const response = { [identificator]: {} };
    // case cp or ccp (customer propery or customer custom property)
    switch(type) {

        case propertyList.cp:
            const cpValue = customerDetails[PropertyCpInputTypeListEnum[propertyType]];
            switch(propertyType) {
                case PropertyCpInputTypeListEnum.dob: response[identificator].value = cpValue && new Date(cpValue); break;
                default: response[identificator].value = cpValue;
            } break;

        case propertyList.ccp:
            const ccpValue = customerDetails.customProperties.find(({propertyId}) => propertyId === property.id)?.value;
            switch(propertyType) {
                case PropertyCcpInputTypeListEnum.Date: response[identificator].value = ccpValue && new Date(ccpValue); break;
                case PropertyCcpInputTypeListEnum.Flag: response[identificator].value = ccpValue === 'true'; break;
                default: response[identificator].value = ccpValue;
            } break;

        default: return {}
    }

    validationObject[identificator] = true;
    response[identificator].validInput = true;
    return response;
}

// helper that checks if the data is the same
const checkIfSameValue = ( identificator, property, value, customerDetails ) => {
    //destructure separator to get type and property type
    const [type, propertyType] = identificator.split('_').map(Number);
    switch(type) {
        case propertyList.cp:
            const cpPropertyType = PropertyCpInputTypeListEnum[propertyType];
            switch(propertyType) {
                case PropertyCpInputTypeListEnum.dob: return (new Date(customerDetails[cpPropertyType]))?.getTime() !== value?.getTime();
                default: return customerDetails[cpPropertyType] !== value;
            }
        case propertyList.ccp:
            const ccpValue = customerDetails.customProperties.find(({propertyId}) => propertyId === propertyType)?.value;
            switch(property.type) {
                case PropertyCcpInputTypeListEnum.Date: return (new Date(ccpValue))?.getTime() !== value?.getTime();
                default: return ccpValue !== value;
            }

        default: return true;
    }
}

const regexExists = (regexIsActive) => isBoolean(regexIsActive) ? regexIsActive : true;
const valueIsEmpty = (string) => ['', undefined].includes(string);

const { customerIdType, newCustomer } = defaultStepPropertyTypeListEnum;
const { dob, gender, notificationConsent, languageCode, CustomerPicture } = PropertyCpInputTypeListEnum;
const { Text: textInput, Flag: flagInput, Date: dateInput, MultiLineText: multiLineTextInput } = PropertyCcpInputTypeListEnum;

//Component that returns input field with proper keys, regex validation, touched indicator, and isMandatory property
const CustomerInputFields = ({ type, property, customerDetails, customerIdTypes, validationObject = {}, dirtyInputsObject = {}, setValidation, isMandatory }) => {

    //form data, if its details step fillout existing customer data
    const [form, setForm] = useState(!!customerDetails ? DetailsDataInjector(type, property, customerDetails, validationObject) : {});

    //destructure needed info for inputs
    const { label, text, regex, regexIsActive, helperText, errorText, placeholder } = property;

    //Helepr function for validation if the field is mandatory
    const required = property.isMandatory || isMandatory;

    // Function to set the touched status for a field
    const touched = (key) => setForm((prev) => ({...prev, [key]: {...(prev?.[key] ?? {}), touched: true}}));

     // Function to handle input change and update the validation object
    const handleInputChange = (name, value, regex) => {
        if (form[name]?.value === value) return

        //indication if field can be empty without triggering error
        const canBeEmpty =  (value?.length > 0) || (value === true) || (value === null) || (typeof value === 'object') || !required || !form[name]?.touched;
        //check if valid input
        const validInput = regexExists(regexIsActive) && !!regex?.length ? new RegExp(regex).test(value) || (valueIsEmpty(value) && canBeEmpty) : canBeEmpty;
        const dirtyInput = !!customerDetails && checkIfSameValue(name, property, value, customerDetails);

        const updatedDirtyInputObject = {...dirtyInputsObject, [name]: dirtyInput}
        const updatedValidationObject = {...validationObject, [name]: validInput}
        //if any field that is required not valid we cannot submit
        const dirtyInputs = Object.values(updatedDirtyInputObject).some((valid) => !!valid);
        //if at least one field that is dirty leave submit on
        const validInputs = Object.values(updatedValidationObject).every((valid) => !!valid);

        //if inputs are valid let customer submit
        setValidation({ validForm: validInputs, validationObject: { ...updatedValidationObject }, dirtyForm: dirtyInputs, dirtyInputsObject: { ...updatedDirtyInputObject } })
        setForm({ ...form, [name]: {value: value, validInput: validInput, touched: true} });
    };

    // Function to handle date picker input change and update the validation object
    const handleDatepickerInputChange = (e, context, identificator) => {
        // if we have some error
        if(context.validationError){
            // if date is invalid
            if(!!e) handleInputChange(identificator, undefined, 'invalid')
            // if inout is empty
            else handleInputChange(identificator, '')
        }
        // if the date is valid
        else handleInputChange(identificator, e);
    }

    // Handle different input types based on the 'type' prop
    switch(type) {
        //Custom customerIdType case that has 2 input fields and activated only once per screen
        case customerIdType: case newCustomer:
            const { idTypeLabel, idTypeHelperText, customerIDLabel, customerIDPlaceholder, customerIDHelperText, customerIDRegexError } = property;
            const { defaultCustomerIdType, filteredCustomerIdTypes, hasCustomerIdTypes } = preProcessData(customerIdTypes, property.customerIdTypes);
            const selectedIdType = form.customerIdType?.value || defaultCustomerIdType || filteredCustomerIdTypes[0].id;
            const customerRegex = filteredCustomerIdTypes.find(x => x.id === selectedIdType).regex;
            const personalIdHasValidInput = chk(form.personalId?.validInput);
            validationObject['customerIdType'] = form.customerIdType?.validInput || true;
            validationObject['personalId'] = form.personalId?.validInput || false;

            const customerIdTypeObj = filteredCustomerIdTypes.find(customerIdType => customerIdType.id === selectedIdType);
            const renderedTitle = customerIdTypeObj?.publicName || customerIdTypeObj?.name;

            return (
                <>
                    <Input style={{display: `${hasCustomerIdTypes ? 'initial' : 'none'}`}}>
                        <Select
                            className={clsx({ 'selected': selectedIdType })}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            name='customerIdType'
                            id= 'select-customer-id-type'
                            inputProps={{id: 'select-customer-id-type'}}
                            label={idTypeLabel}
                            value={selectedIdType || ''}
                            helperText={idTypeHelperText}
                            onChange={e => { handleInputChange('customerIdType',e.target?.value); }}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: () => renderedTitle

                                }
                            }
                        >
                        {
                            filteredCustomerIdTypes.map((customerIdType) =>
                                <MenuItem key={customerIdType.id} value={customerIdType.id}>
                                    {customerIdType.publicName || customerIdType.name}
                                </MenuItem>
                            )
                        }
                        </Select>
                    </Input>
                    <Input>
                        <SearchField
                            name='personalId'
                            id='text-search-customer'
                            inputProps={{ maxLength: 100 }}
                            label={customerIDLabel}
                            placeholder={customerIDPlaceholder}
                            value={form['personalId']?.value || ''}
                            handleChange={(e) => { handleInputChange('personalId', e.target?.value, customerRegex) }}
                            error={!personalIdHasValidInput}
                            onBlur={(e) => { touched('personalId'); handleInputChange('personalId',e.target?.value, customerRegex); }}
                            helperText={personalIdHasValidInput ? customerIDHelperText : customerIDRegexError}
                            required
                        />
                    </Input>
                </>

            )
        //Customer properties
        case propertyList.cp:
            const cpIdentificator = `${type}_${property.type}`;
            const cpValidInput = chk(form[cpIdentificator]?.validInput);
            validationObject[cpIdentificator] = validateIfNotRequired(form[cpIdentificator]?.validInput, required);
            switch(property.type){
                //Calendar property
                case dob:
                    return (
                        <Input>
                            <MobileDatePicker {...{label, required}}
                                name={`${cpIdentificator}`}
                                id={`date-picker-${cpIdentificator}`}
                                onChange={(e, context) => handleDatepickerInputChange(e, context, cpIdentificator)}
                                value={form[cpIdentificator]?.value || null}
                                PopperProps={popperProps()}
                                error={!cpValidInput}
                                helperText={cpValidInput ? helperText : errorText}
                                disableFuture
                            />
                        </Input>

                    )
                //Select property
                case gender: case notificationConsent: case languageCode:

                    const selectedValue = getSelectedValue(property.type, form[cpIdentificator]?.value, customerDetails)

                    const hasSelectableValues = !(property.valueList?.length === 1);

                    const valueList = required || property.type === gender
                        ? property.valueList
                        : [{Id: '', Name: null}, ...property.valueList];

                    validationObject[cpIdentificator] = validateIfNotRequired(form[cpIdentificator]?.validInput, required);

                    return (
                        <Input>
                            <Select {...{label, helperText, required}}
                                className={clsx({ 'selected': selectedValue })}
                                fullWidth
                                id={property.type}
                                inputProps={{id: property.type, shrink: true}}
                                name={cpIdentificator}
                                value={selectedValue}
                                onChange={e => handleInputChange(cpIdentificator, e.target?.value || null, regex) }
                                onBlur={(e) => { touched(cpIdentificator); handleInputChange(cpIdentificator, e.target?.value || null, regex) }}
                                SelectProps={{
                                    readOnly: !hasSelectableValues,

                                    renderValue: () =>  {
                                        const element = property.valueList?.find(({Id}) => selectedValue === Id)
                                        const value =  element?.Name || placeholder
                                        return <div>{value}</div>
                                    }
                                }}
                            >
                                {
                                    valueList?.map((obj) =>
                                        <MenuItem key={obj.Id} value={obj.Id}>
                                            {obj.Name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                        </Input>
                    )
                case CustomerPicture :
                    return (
                        <></>
                    )

                default:
                    const { extRef, firstName, lastName, email, telNumber1, telNumber2 } = PropertyCpInputTypeListEnum;
                    let type, maxLength;

                    switch(property.type){
                        case telNumber1: case telNumber2:
                            maxLength = 25;
                            type = "tel";
                            break;
                        case firstName: case lastName:
                            maxLength = 50;
                            break;
                        case email:
                            maxLength = 100;
                            type = "email";
                            break;
                        case extRef:
                            maxLength = 200;
                            break;
                        default:
                            break;
                    }

                    return (
                        <Input>
                            <SearchField {...{label, placeholder, required}}
                                name={`${cpIdentificator}`}
                                id={`text-input-${cpIdentificator}`}
                                value={form[cpIdentificator]?.value || ''}
                                handleChange={(e) => { handleInputChange(cpIdentificator, e.target?.value, regex) }}
                                onBlur={(e) => { touched(cpIdentificator); handleInputChange(cpIdentificator, e.target?.value, regex) }}
                                error={!cpValidInput}
                                helperText={cpValidInput ? helperText : errorText}
                                inputProps={{type, maxLength}}
                            />
                        </Input>
                    )
            }
        //Custom customer properties
        case propertyList.ccp:
            const ccpIdentificator = `${type}_${property.id}`;
            const ccpValidInput = chk(form[ccpIdentificator]?.validInput);
            validationObject[ccpIdentificator] = validateIfNotRequired(form[ccpIdentificator]?.validInput, required);
            switch(property.type){
                //Input field
                case textInput:
                    return (
                        <Input>
                            <SearchField {...{label, placeholder, required}}
                                name={`${ccpIdentificator}`}
                                id={`text-input-${ccpIdentificator}`}
                                value={form[ccpIdentificator]?.value || ''}
                                handleChange={(e) => { handleInputChange(ccpIdentificator, e.target?.value, regex) }}
                                onBlur={(e) => { touched(ccpIdentificator); handleInputChange(ccpIdentificator, e.target?.value, regex) }}
                                error={!ccpValidInput}
                                helperText={ccpValidInput ? helperText : errorText}
                            />
                        </Input>
                    )
                //Checkbox property
                case flagInput:
                    return (
                        <Input>
                            <CheckBox {...{required, helperText}}
                                name={`${ccpIdentificator}`}
                                handlechange={(val) => handleInputChange(ccpIdentificator, val, (val === false && required && 'invalid') || '')}
                                value={!!form[ccpIdentificator]?.value}
                                label={text}
                            />
                        </Input>
                    )
                //Calendar propery
                case dateInput:
                    return (
                        <Input>
                            <MobileDatePicker {...{label, required}}
                                name={`${ccpIdentificator}`}
                                id={`date-picker-${ccpIdentificator}`}
                                onChange={(e, context) => handleDatepickerInputChange(e, context, ccpIdentificator)}
                                value={form[ccpIdentificator]?.value || null}
                                PopperProps={popperProps()}
                                error={!ccpValidInput}
                                helperText={ccpValidInput ? helperText : errorText}
                            />
                        </Input>
                    )
                //Multiline property
                case multiLineTextInput:
                    return (
                        <Input>
                            <MultiLineText {...{label, placeholder, required}}
                                name={`${ccpIdentificator}`}
                                value={form[ccpIdentificator]?.value || ''}
                                onChange={(e)=> handleInputChange(ccpIdentificator, e.target?.value, regex)}
                                onBlur={(e) => { touched(ccpIdentificator); handleInputChange(ccpIdentificator, e.target?.value, regex) }}
                                maxRows={4}
                                error={!ccpValidInput}
                                helperText={ccpValidInput ? helperText : errorText}
                            />
                        </Input>
                    )
                default:
                    return (
                        <Input>
                            <SearchField {...{label, placeholder, required}}
                                name={`${ccpIdentificator}`}
                                id={`text-input-${ccpIdentificator}`}
                                value={form[ccpIdentificator]?.value || ''}
                                handleChange={(e) => { handleInputChange(ccpIdentificator, e.target?.value, regex) }}
                                onBlur={(e) => { touched(ccpIdentificator); handleInputChange(ccpIdentificator, e.target?.value, regex) }}
                                error={!ccpValidInput}
                                helperText={ccpValidInput ? helperText : errorText}
                            />
                        </Input>
                    )
            }
        default:
            return null;
    }
}
function getSelectedValue(type, formValue, customerDetails) {
    if(type === gender)
    {
        if(formValue)
            return `${formValue}`;
        return `${customerDetails?.gender}`
    }
    if(type === notificationConsent)
    {
        if(formValue)
            return `${formValue}`;
        return `${customerDetails?.notificationConsent}`
    }
    return formValue || '';

 }

function errorTrigger() { throw new Error('No customer id types'); }

//compare and merge two customerIdTypes objects for missing, default, different language and more...
function preProcessData(customerIdTypesArray, widgetCustomerIdTypes){
    let defaultCustomerIdType;

    const customerIdTypesQflow = customerIdTypesArray.reduce((obj, item) => {
        item.isActive && (obj[item.id] = item);
        return obj;
    }, {});

    const filteredCustomerIdTypes = widgetCustomerIdTypes.reduce((arr, item) => {
        const customerIdTypeExists = !!customerIdTypesQflow[item.id];
      if (item.isActive && customerIdTypeExists) {
        const { publicName, name, isDefault } = customerIdTypesQflow[item.id] || { undefined }

        !!isDefault && ( defaultCustomerIdType = item.id );
        item.publicName = publicName || name;

        arr.push(item);
      }

      return arr;

    }, [])

    !filteredCustomerIdTypes.length && errorTrigger();
    const hasCustomerIdTypes = filteredCustomerIdTypes.length > 1;

    return { defaultCustomerIdType, filteredCustomerIdTypes, hasCustomerIdTypes }
  }




export default CustomerInputFields;